import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import useAdminStore from '../stores/useAdminStore';
import useAuthStore from '../stores/useAuthStore';
import { useToast } from '../components/ToastContainer';
import DOMPurify from 'dompurify';
import useCourseStore from '../stores/useCourseStore';
import axiosClient from '../services/api';
import exportToPDF from '../utils/exportToPDF';

const AdminCourseManagement = () => {
  const { user } = useAuthStore();
  const toast = useToast();
  const [allFlattenedCategories, setAllFlattenedCategories] = useState([]);
  const {
    pendingCourses,
    fetchPendingCourses,
    approveCourse,
    rejectCourse,
    isLoading,
    clearError,
    error
  } = useAdminStore();

  const {
    fetchCategories,
    categories: allCategories,
    isLoading: isloadingCategory,
  } = useCourseStore();

  const [loadingType, setLoadingType] = useState(null);
  const [previewQuiz, setPreviewQuiz] = useState(null);
  const [previewAssignment, setPreviewAssignment] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [previewResource, setPreviewResource] = useState(null);

  const [confirmInstructorChange, setConfirmInstructorChange] = useState(null);
  const [loading, setLoading] = useState(false);

  const [instructors, setInstructors] = useState([]);

  useEffect(() => {
    const fetchInstructors = async () => {
      const res = await axiosClient.get('/users/admin/users?role=instructor');
      setInstructors(res.data.users);
    };
    fetchInstructors();
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, toast, clearError]);

  const [filters, setFilters] = useState({
    status: 'pending',
    category: 'all',
    instructor: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [previewCourse, setPreviewCourse] = useState(null);
  const [rejectionComments, setRejectionComments] = useState('');

  useEffect(() => {
    fetchPendingCourses();
  }, [fetchPendingCourses]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const flattenCategories = useCallback((category, depth = 0) => {
    let result = [{ ...category, depth }];
    if (category.children && category.children.length > 0) {
      category.children.forEach((child) => {
        result = result.concat(flattenCategories(child, depth + 1));
      });
    }
    return result;
  }, []);

  useEffect(() => {
    if (allCategories.length > 0) {
      const flatCategories = allCategories.flatMap(cat => flattenCategories(cat));
      setAllFlattenedCategories(flatCategories);
    }
  }, [allCategories, flattenCategories]);

  const handleApprove = async (courseId) => {
    if (window.confirm('Are you sure you want to approve this course?')) {
      try {
        await approveCourse(courseId);
        fetchPendingCourses();
        toast.success('course appoved successfuly')
      } catch (error) {
        console.error('Approval failed:', error);
      }
    }
  };

  const handleReject = async (courseId) => {
    if (!rejectionComments.trim()) {
      alert('Please provide rejection comments.');
      return;
    }
    if (window.confirm('Are you sure you want to reject this course?')) {
      try {
        await rejectCourse(courseId, rejectionComments);
        fetchPendingCourses();
        setRejectionComments('');
        toast.error('course rejected successfuly')
      } catch (error) {
        console.error('Rejection failed:', error);
      }
    }
  };

  const handleDownload = async (resourceId) => {
    try {
      const { data } = await axiosClient.get(`/resources/${resourceId}/signed-url`);

      const signedUrl = data.data.signedUrl;

      window.open(signedUrl, "_blank");
    } catch (error) {
      toast.error("Failed to open resource");
    }
  };

  const filteredCourses = pendingCourses.filter(course => {
    const matchesStatus = filters.status === '' || course.status === filters.status;
    const matchesCategory = filters.category === 'all' || course.category?._id === filters.category;
    const matchesInstructor = !filters.instructor ||
      course.instructor?.name?.toLowerCase().includes(filters.instructor.toLowerCase()) ||
      course.instructor?.email?.toLowerCase().includes(filters.instructor.toLowerCase());
    const matchesSearch = !searchTerm ||
      course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesCategory && matchesInstructor && matchesSearch;
  });


  // Export filtered courses as PDF
  const exportCourses = () => {
    if (filteredCourses.length === 0) return;

    const columns = ['Title', 'Instructor', 'Category', 'Type', 'Status', 'Created At'];
    const rows = filteredCourses.map(course => [
      course.title || 'N/A',
      course.instructor?.name || 'N/A',
      course.category.name || 'N/A',
      course.courseType || 'N/A',
      course.status || 'N/A',
      course.createdAt ? new Date(course.createdAt).toLocaleDateString() : 'N/A'
    ]);

    exportToPDF(filteredCourses, 'Course_Management_Report', columns, rows);
  };

  if (isLoading || isloadingCategory) {
    return (
      <DashboardLayout userRole={user.role}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userRole={user.role}>
      <div className="py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Course Management</h1>
            <div className='flex justify-between items-end'>
              <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                Review and manage course submissions from instructors
              </p>

              <button
                onClick={exportCourses}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                Export as PDF
              </button>
            </div>

          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="approved">Approved</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="all">All Categories</option>
                  {allFlattenedCategories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {`${"— ".repeat(cat.depth)}${cat.name}`}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Instructor</label>
                <input
                  type="text"
                  value={filters.instructor}
                  onChange={(e) => setFilters({ ...filters, instructor: e.target.value })}
                  placeholder="Search instructor..."
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Search</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Course title or description..."
                  className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Courses Table */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {filteredCourses.length > 0 ? (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-neutral-200 dark:divide-neutral-700">
                  <thead className="bg-neutral-50 dark:bg-neutral-700/50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Course</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Instructor</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Category</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-neutral-800 divide-y divide-neutral-200 dark:divide-neutral-700">
                    {filteredCourses.map((course) => (
                      <tr key={course._id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-neutral-900 dark:text-white">{course.title}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-neutral-900 dark:text-white">{course.instructor?.name}</div>
                          <div className="text-sm text-neutral-500 dark:text-neutral-400">{course.instructor?.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                            {course?.category?.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${course.status === 'pending'
                            ? 'bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-300'
                            : course.status === 'approved'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                            {course.status.charAt(0).toUpperCase() + course.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 flex flex-col gap-2 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => setPreviewCourse(course)}
                            className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                          >
                            Preview
                          </button>
                          {course.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(course._id)}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  setRejectionComments('');
                                  setPreviewCourse(course);
                                }}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Reject
                              </button>
                            </>

                          )}

                          {course.status === 'approved' && (
                            <>
                              <button
                                onClick={() => {
                                  setRejectionComments('');
                                  setPreviewCourse(course);
                                }}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Reject
                              </button>
                            </>
                          )}

                          {course.status === 'rejected' && (
                            <>
                              <button
                                onClick={() => handleApprove(course._id)}
                                className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => {
                                  setRejectionComments('');
                                  setPreviewCourse(course);
                                }}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700">
              <div className="text-6xl mb-4">📚</div>
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">No {filters.status} courses found </h3>
              <p className="text-neutral-600 dark:text-neutral-400">
                Try adjusting your filters or check back later.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Course Preview Modal */}
      {previewCourse && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-neutral-900 opacity-75" onClick={() => setPreviewCourse(null)}></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div
              className="inline-block align-bottom bg-white dark:bg-neutral-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full max-h-[90vh] overflow-y-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <div className="bg-white dark:bg-neutral-800 px-6 py-4 border-b border-neutral-200 dark:border-neutral-700 flex justify-between items-center">
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">Course Preview</h3>
                <button
                  type="button"
                  className="text-neutral-400 hover:text-neutral-500 dark:hover:text-neutral-300 focus:outline-none"
                  onClick={() => setPreviewCourse(null)}
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="px-6 py-4">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">{previewCourse.title}</h2>
                  <p className="text-neutral-600 dark:text-neutral-400 mt-1"><div
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewCourse?.description) }}
                  /></p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 text-sm font-medium rounded-full">
                      {previewCourse?.category?.name}
                    </span>
                    <span className="px-3 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 text-sm font-medium rounded-full">
                      {previewCourse.level}
                    </span>
                    <span className="px-3 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 text-sm font-medium rounded-full">
                      {previewCourse.duration}
                    </span>
                  </div>
                </div>

                <div className="mb-6">

                  <div className="flex items-center justify-between">

                    <div>
                      <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Instructor</h3>
                      <div className='flex items-center'>
                        <div className="w-10 h-10 rounded-full bg-neutral-200 dark:bg-neutral-700 flex items-center justify-center">
                          <span className="text-neutral-500 dark:text-neutral-400 font-medium">
                            {previewCourse.instructor?.name?.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-neutral-900 dark:text-white">{previewCourse.instructor?.name}</p>
                          <p className="text-sm text-neutral-500 dark:text-neutral-400">{previewCourse.instructor?.email}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="text font-medium text-neutral-900 dark:text-white mb-3">
                        Change Instructor
                      </h3>
                      <select
                        value={previewCourse.instructor?._id || ''}
                        onChange={(e) => {
                          const newInstructorId = e.target.value;
                          const newInstructor = instructors.find(i => i._id === newInstructorId);
                          if (newInstructorId === previewCourse.instructor?._id) return;
                          setConfirmInstructorChange({ newInstructorId, newInstructor });
                        }}
                        className="w-[200px] px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        {instructors?.map((inst) => (
                          <option key={inst?._id} value={inst?._id}>
                            {inst?.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Curriculum</h3>
                  <div className="space-y-4">
                    {previewCourse.modules?.map((module, moduleIndex) => (
                      <div key={moduleIndex} className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
                        <h4 className="font-medium text-neutral-900 dark:text-white mb-2">
                          Module {moduleIndex + 1}: {module.title}
                        </h4>
                        <div className="space-y-2">
                          {module.lessons?.map((lesson, lessonIndex) => (
                            <div key={lessonIndex} className="border border-neutral-200 dark:border-neutral-700 rounded p-3">
                              <div className="flex items-start">
                                <div className="mr-3 mt-1">
                                  {lesson.video ? (
                                    <svg className="h-4 w-4 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                  ) : (
                                    <svg className="h-4 w-4 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                  )}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-neutral-900 dark:text-white">{lesson.title}</p>

                                  {/* Quiz Preview - CLICKABLE */}
                                  {lesson.quiz && (
                                    <div
                                      className="mt-2 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/30"
                                      onClick={async () => {
                                        try {
                                          setLoadingType('quiz');
                                          const res = await axiosClient.get(`/learning/quizzes/${lesson.quiz}`);
                                          setPreviewQuiz(res.data.quiz);
                                        } catch (err) {
                                          console.error('Failed to fetch quiz:', err);
                                        } finally {
                                          setLoadingType(null);
                                        }
                                      }}
                                    >
                                      <p className="text-xs font-medium text-amber-700 dark:text-amber-300 mb-1">
                                        {loadingType === 'quiz' ? 'Loading Quiz...' : 'View Quiz'}
                                      </p>
                                    </div>
                                  )}

                                  {/* Assignment Preview - CLICKABLE */}
                                  {lesson.assignment && (
                                    <div
                                      className="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/30"
                                      onClick={async () => {
                                        try {
                                          setLoadingType('assignment');
                                          const res = await axiosClient.get(`/learning/assignments/${lesson.assignment}`);
                                          setPreviewAssignment(res.data.assignment);
                                        } catch (err) {
                                          console.error('Failed to fetch assignment:', err);
                                        } finally {
                                          setLoadingType(null);
                                        }
                                      }}
                                    >
                                      <p className="text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                                        {loadingType === 'assignment' ? 'Loading Assignment...' : 'View Assignment'}
                                      </p>
                                    </div>
                                  )}

                                  {/* Video Preview - CLICKABLE */}
                                  {lesson.video && (
                                    <div className='flex gap-3'>
                                      <div
                                        className="mt-2 p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                        onClick={async () => {
                                          try {
                                            setLoadingType('video');
                                            const res = await axiosClient.get(`/videos/${lesson.video}/manifest`);
                                            setPreviewVideo(res.data.data);
                                          } catch (err) {
                                            console.error('Failed to fetch video:', err);
                                          } finally {
                                            setLoadingType(null);
                                          }
                                        }}
                                      >
                                        <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                          {loadingType === 'video' ? 'Loading Video Manifest...' : 'View Video Manifest'}
                                        </p>
                                      </div>

                                      <div
                                        className="mt-2 p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                        onClick={async () => {
                                          try {
                                            setLoadingType('video');
                                            const { data } = await axiosClient.get(`/videos/${lesson.video}/signed-url/720p`);
                                            const signedUrl = data?.data?.signedUrl;
                                            window.open(signedUrl, "_blank");
                                          } catch (err) {
                                            console.error('Failed to fetch video:', err);
                                          } finally {
                                            setLoadingType(null);
                                          }
                                        }}
                                      >
                                        <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                          {loadingType === 'video' ? 'Loading Video...' : 'View Video'}
                                        </p>
                                      </div>
                                    </div>

                                  )}

                                  {/* Resources Preview - CLICKABLE */}
                                  {lesson.resources && lesson.resources.length > 0 && (
                                    <div
                                      className="mt-2 p-2 bg-neutral-50 dark:bg-neutral-700/50 rounded cursor-pointer hover:bg-neutral-100 dark:hover:bg-neutral-700"
                                      onClick={async () => {
                                        try {
                                          setLoadingType('resource');
                                          const resourceId = lesson.resources[0]._id || lesson.resources[0];
                                          const res = await axiosClient.get(`/resources/${resourceId}/meta`);
                                          setPreviewResource(res.data.data);
                                        } catch (err) {
                                          console.error('Failed to fetch resource:', err);
                                        } finally {
                                          setLoadingType(null);
                                        }
                                      }}
                                    >
                                      <p className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                                        {loadingType === 'resource'
                                          ? 'Loading Resources...'
                                          : `View Resources (${lesson.resources.length})`}
                                      </p>
                                    </div>
                                  )}

                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {(previewCourse.status === 'pending' || previewCourse.status === 'approved' || previewCourse.status === 'rejected') && (
                  <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
                    <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">Rejection Comments</h3>
                    <textarea
                      value={rejectionComments}
                      onChange={(e) => setRejectionComments(e.target.value)}
                      rows={3}
                      placeholder="Enter comments for the instructor (required for rejection)..."
                      className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setPreviewCourse(null)}
                        className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-800 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm hover:bg-neutral-50 dark:hover:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        Cancel
                      </button>

                      {previewCourse.status === 'pending' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleApprove(previewCourse._id)}
                            className="px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            Approve Course
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(previewCourse._id)}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            Reject Course
                          </button>
                        </>
                      )}

                      {previewCourse.status === 'approved' && (
                        <button
                          type="button"
                          onClick={() => handleReject(previewCourse._id)}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          Reject Course
                        </button>
                      )}

                      {previewCourse.status === 'rejected' && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleApprove(previewCourse._id)}
                            className="px-4 py-2 text-sm font-medium text-white bg-green-500 hover:bg-green-600 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                          >
                            Approve Course
                          </button>
                          <button
                            type="button"
                            onClick={() => handleReject(previewCourse._id)}
                            className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            Reject Course
                          </button>
                        </>
                      )}

                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quiz Preview Modal */}
      {previewQuiz && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white dark:bg-neutral-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-6 relative">
            <button
              onClick={() => setPreviewQuiz(null)}
              className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-4">Quiz Preview</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-white">{previewQuiz.title}</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{previewQuiz.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Time Limit</p>
                  <p className="font-medium">{previewQuiz.timeLimit} minutes</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Passing Score</p>
                  <p className="font-medium">{previewQuiz.passingScore}%</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Questions</p>
                  <p className="font-medium">{previewQuiz.questions?.length || 0}</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Attempts</p>
                  <p className="font-medium">{previewQuiz.attemptsAllowed}</p>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-white mb-2">Questions</h4>
                {previewQuiz.questions?.map((q, idx) => (
                  <div key={idx} className="p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded mb-2">
                    <p className="text-neutral-900 dark:text-white mb-2">{q.question}</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Type: {q.type} | Points: {q.points}
                    </p>
                    {q.type === 'multiple-choice' && (
                      <ul className="mt-2 text-xs">
                        {q.options.map((opt, optIndex) => (
                          <li key={optIndex} className={opt.isCorrect ? 'text-green-600 dark:text-green-400' : ''}>
                            {optIndex + 1}. {opt.text} {opt.isCorrect && '(Correct)'}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assignment Preview Modal */}
      {previewAssignment && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white dark:bg-neutral-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-6 relative">
            <button
              onClick={() => setPreviewAssignment(null)}
              className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-4">Assignment Preview</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-white">{previewAssignment.title}</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{previewAssignment.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Due Date</p>
                  <p className="font-medium">
                    {previewAssignment.dueDate ? new Date(previewAssignment.dueDate).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Points</p>
                  <p className="font-medium">{previewAssignment.points}</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Late Submissions</p>
                  <p className="font-medium">
                    {previewAssignment.allowLateSubmissions ? 'Allowed' : 'Not Allowed'}
                  </p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Submission Types</p>
                  <p className="font-medium">{previewAssignment.submissionTypes?.join(', ')}</p>
                </div>
              </div>
              {previewAssignment.instructions && (
                <div>
                  <h4 className="font-medium text-neutral-900 dark:text-white mb-2">Instructions</h4>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">
                    {previewAssignment.instructions}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {previewVideo && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white dark:bg-neutral-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-6 relative">
            <button
              onClick={() => setPreviewVideo(null)}
              className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-4">Video Preview</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-white">{previewVideo.title}</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{previewVideo.originalName}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Duration</p>
                  <p className="font-medium">{previewVideo.duration} seconds</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">File Size</p>
                  <p className="font-medium">{(previewVideo.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Encoding Status</p>
                  <p className="font-medium">{previewVideo.encodingStatus}</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Resolutions</p>
                  <p className="font-medium">{previewVideo.availableResolutions?.join(', ')}</p>
                </div>
              </div>
              {previewVideo.availableResolutions?.length > 0 && (
                <div>
                  <h4 className="font-medium text-neutral-900 dark:text-white mb-2">Available Resolutions</h4>
                  <div className="flex flex-wrap gap-2">
                    {previewVideo.availableResolutions.map(res => (
                      <span key={res} className="px-2 py-1 bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300 text-xs rounded">
                        {res}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resource Preview Modal */}
      {previewResource && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white dark:bg-neutral-800 rounded-lg max-w-2xl w-full max-h-[80vh] overflow-auto p-6 relative">
            <button
              onClick={() => setPreviewResource(null)}
              className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white mb-4">Resource Preview</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-neutral-900 dark:text-white">{previewResource.title}</h4>
                <p className="text-sm text-neutral-600 dark:text-neutral-400">{previewResource.originalName}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">File Size</p>
                  <p className="font-medium">{(previewResource.fileSize / 1024 / 1024).toFixed(2)} MB</p>
                </div>
                <div>
                  <p className="text-neutral-500 dark:text-neutral-400">Folder</p>
                  <p className="font-medium">{previewResource.folder}</p>
                </div>
              </div>
              <div className="flex gap-3">

                {/* Download */}
                <button
                  onClick={() => handleDownload(previewResource._id)}
                  className="inline-flex items-center px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-600"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  View Resource
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {confirmInstructorChange && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-3">
              Confirm Instructor Change
            </h3>
            <p className="text-neutral-700 dark:text-neutral-300 mb-4">
              You are about to transfer full ownership of this course to{" "}
              <span className="font-semibold">"{confirmInstructorChange.newInstructor?.name}"</span>.
            </p>

            <h4 className="font-medium text-neutral-900 dark:text-white mb-2">The following will be updated:</h4>
            <ul className="text-sm text-neutral-600 dark:text-neutral-400 list-disc pl-5 space-y-1 mb-6">
              <li>Course instructor</li>
              <li>All course videos (uploadedBy)</li>
              <li>All quizzes (uploadedBy)</li>
              <li>All assignments (uploadedBy)</li>
              <li>All assignment submissions graded by current instructor (gradedBy)</li>
              <li>All resources (uploadedBy)</li>
              <li>Discussion threads authored by current instructor in this course</li>
              <li>Discussion replies authored by current instructor in this course</li>
            </ul>

            <p className="text-sm text-red-500 dark:text-red-400 mb-6">
              This action cannot be undone.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setConfirmInstructorChange(null)}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-red-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={async () => {
                  setLoading(true);
                  try {
                    await axiosClient.put(`/courses/admin/courses/${previewCourse._id}/instructor`, {
                      instructorId: confirmInstructorChange.newInstructorId,
                    });
                    setPreviewCourse((prev) => ({
                      ...prev,
                      instructor: confirmInstructorChange.newInstructor,
                    }));
                    fetchPendingCourses();
                    toast.success('Instructor and all related content updated successfully');
                    setConfirmInstructorChange(null);
                  } catch (err) {
                    console.error('Failed to update instructor:', err);
                    toast.error('Failed to update instructor. Please try again.');
                  } finally {
                    setLoading(false);
                  }
                }}
                className={`px-4 py-2 text-sm font-medium rounded flex items-center ${loading
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-primary-500 hover:bg-primary-700 text-white'
                  }`}
              >
                {loading ? (
                  <>
                   <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500"></div>
                    Updating...
                  </>
                ) : (
                  'Confirm Change'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

    </DashboardLayout>
  );
};

export default AdminCourseManagement;